import { supabase } from '../lib/supabase/client';

// User data interface (compatible with client views)
export interface UserData {
  uid: string;
  email: string;
  displayName?: string;
  credits: number;
  subscriptionStatus: 'free' | 'basic' | 'premium' | 'pro';
  subscriptionId?: string;
  subscriptionStartDate?: Date;
  subscriptionEndDate?: Date;
  totalCreditsUsed: number;
  createdAt: Date;
  lastLoginAt: Date;
}

// Credit transaction interface (compatible with client views)
export interface CreditTransaction {
  id?: string;
  userId: string;
  type: 'earned' | 'spent' | 'purchased' | 'bonus';
  amount: number;
  description: string;
  relatedAction?: string; // e.g., 'image_conversion'
  timestamp: Date;
}

// Subscription plan interface
export interface SubscriptionPlan {
  id: string;
  name: string;
  credits: number;
  price: number;
  duration: number; // days
  features: string[];
}

// Initialize user profile in Supabase
export const createUserProfile = async (user: any): Promise<void> => {
  try {
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('user_id', user.id || user.uid)
      .maybeSingle();

    if (!data && !error) {
      const userId = user.id || user.uid;
      const userEmail = user.email || '';
      
      await supabase.from('user_profiles').insert({
        user_id: userId,
        email: userEmail,
        credits: 5,
        subscription_status: 'none'
      });
      
      // Welcome bonus transaction
      await addCreditTransaction(userId, {
        userId,
        type: 'bonus',
        amount: 5,
        description: 'Welcome bonus credits',
        relatedAction: 'user_registration',
        timestamp: new Date()
      });
    }
  } catch (err) {
    console.error('Error creating user profile in Supabase:', err);
  }
};

// Get user data from Supabase
export const getUserData = async (userId: string): Promise<UserData | null> => {
  try {
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();
      
    if (error) throw error;
    if (!data) return null;
    
    return {
      uid: data.user_id,
      email: data.email || '',
      displayName: '',
      credits: data.credits || 0,
      subscriptionStatus: (data.subscription_status === 'premium' ? 'premium' : 'free') as any,
      totalCreditsUsed: 0,
      createdAt: new Date(data.created_at),
      lastLoginAt: new Date(data.updated_at)
    };
  } catch (error) {
    console.error('Error getting user data from Supabase:', error);
    return null;
  }
};

// Update user credits in Supabase
export const updateUserCredits = async (userId: string, creditsToAdd: number): Promise<void> => {
  try {
    const { error } = await supabase.rpc('increment_credits', {
      user_id_param: userId,
      amount_param: creditsToAdd
    });

    if (error) {
      console.warn('increment_credits RPC failed, falling back to manual read-update:', error);
      // Fallback manual read-write
      const currentData = await getUserData(userId);
      if (currentData) {
        await supabase
          .from('user_profiles')
          .update({
            credits: (currentData.credits || 0) + creditsToAdd,
            updated_at: new Date().toISOString()
          })
          .eq('user_id', userId);
      }
    }
  } catch (error) {
    console.error('Error updating credits in Supabase:', error);
    throw error;
  }
};

// Spend user credits in Supabase
export const spendUserCredits = async (userId: string, creditsToSpend: number, action: string): Promise<boolean> => {
  try {
    const userData = await getUserData(userId);
    if (!userData || userData.credits < creditsToSpend) {
      return false; // Insufficient credits
    }

    await updateUserCredits(userId, -creditsToSpend);

    // Record spending transaction
    await addCreditTransaction(userId, {
      userId,
      type: 'spent',
      amount: -creditsToSpend,
      description: `Credits spent on ${action}`,
      relatedAction: action,
      timestamp: new Date()
    });

    return true;
  } catch (error) {
    console.error('Error spending credits in Supabase:', error);
    throw error;
  }
};

// Add credit transaction to Supabase
export const addCreditTransaction = async (userId: string, transaction: Omit<CreditTransaction, 'id'>): Promise<void> => {
  try {
    await supabase.from('credit_transactions').insert({
      user_id: userId,
      type: transaction.type,
      amount: transaction.amount,
      description: transaction.description,
      related_action: transaction.relatedAction
    });
  } catch (error) {
    console.error('Error adding credit transaction to Supabase:', error);
    throw error;
  }
};

// Get user credit transactions from Supabase
export const getUserCreditTransactions = async (userId: string, limitCount: number = 50): Promise<CreditTransaction[]> => {
  try {
    const { data, error } = await supabase
      .from('credit_transactions')
      .select('*')
      .eq('user_id', userId)
      .order('timestamp', { ascending: false })
      .limit(limitCount);
      
    if (error) throw error;
    if (!data) return [];
    
    return data.map(doc => ({
      id: doc.id,
      userId: doc.user_id,
      type: doc.type as any,
      amount: doc.amount,
      description: doc.description,
      relatedAction: doc.related_action,
      timestamp: new Date(doc.timestamp)
    }));
  } catch (error) {
    console.error('Error getting credit transactions from Supabase:', error);
    return [];
  }
};

// Update subscription status in Supabase
export const updateSubscriptionStatus = async (
  userId: string, 
  status: UserData['subscriptionStatus'],
  subscriptionId?: string,
  endDate?: Date
): Promise<void> => {
  try {
    await supabase
      .from('user_profiles')
      .update({
        subscription_status: status === 'premium' ? 'premium' : 'free',
        updated_at: new Date().toISOString()
      })
      .eq('user_id', userId);
  } catch (error) {
    console.error('Error updating subscription status in Supabase:', error);
    throw error;
  }
};

// Check if user has enough credits in Supabase
export const hasEnoughCredits = async (userId: string, requiredCredits: number): Promise<boolean> => {
  try {
    const userData = await getUserData(userId);
    return userData ? userData.credits >= requiredCredits : false;
  } catch (error) {
    console.error('Error checking credits in Supabase:', error);
    return false;
  }
};

// Get static subscription plans
export const getSubscriptionPlans = (): SubscriptionPlan[] => {
  return [
    {
      id: 'basic',
      name: 'Basic Plan',
      credits: 100,
      price: 9.99,
      duration: 30,
      features: ['100 credits per month', 'Basic image processing', 'Email support']
    },
    {
      id: 'premium',
      name: 'Premium Plan',
      credits: 500,
      price: 19.99,
      duration: 30,
      features: ['500 credits per month', 'Advanced image processing', 'Priority support', 'Batch processing']
    },
    {
      id: 'pro',
      name: 'Pro Plan',
      credits: 2000,
      price: 49.99,
      duration: 30,
      features: ['2000 credits per month', 'All features', '24/7 support', 'API access', 'Custom integrations']
    }
  ];
};
